/**
 * Canonical date policy for FactSet Query Language formulas.
 *
 * FactSet time-series formulas consume Excel's underlying numeric date value
 * more reliably than display-formatted date text. Date displays remain a
 * workbook-formatting concern; FQL normally receives the raw Excel serial from
 * a bound cell, a relative FactSet period token, or an exact TTF-returned
 * MM/DD/YYYY literal.
 */

export const FQL_DATE_INPUT_GUIDANCE = `DATE INPUTS AND TIME-SERIES DATE AXES:
- Bind absolute date, as-of-date, start-date, and end-date placeholders to real Excel date cells. Prefer =DATE(year,month,day) when constructing an input; never hand-compute or type an Excel serial.
- A date cell's number format controls display only. Keep the cell numeric and apply a readable date format such as dd-mmm-yyyy.
- When embedding an absolute date cell inside an FDS/FDSC/FDSZ string argument, concatenate the raw cell reference so FactSet receives its underlying Excel serial: "P_PRICE("&$G$7&","&$G$8&",D)". Do NOT wrap the date cell in TEXT().
- If an exact TTF-returned formula contains an inline absolute date, only a calendar-valid MM/DD/YYYY literal is supported. Direct numeric date-cell binding remains preferred.
- Relative FQL periods such as -9D, 0D, -1AY, and NOW remain text tokens and are concatenated unchanged.
- Keep range inputs in chronological order: Start Date must be earlier than or equal to End Date. For a recent ten-day relative window, use -9D through 0D, not the reverse.
- When asking generate_formula for a windowed series, state the window as explicitly labelled tokens — "Start Date -9D and End Date 0D" — on the FIRST attempt. A natural-language span such as "for the last 10 trading days through today" frequently comes back with the endpoints reversed, wasting a round trip.
- generate_formula restores a TTF response that contains the exact requested pair in reverse when the query explicitly states the ordered range. If a reversed-range error remains, retry once with clearly labelled start and end dates; do not stop after the first recoverable formula-generation error.
- For a time-series table, obtain the displayed Date column by wrapping the exact same returned value-series expression as julian(<series>.dates), preserving its identifier, start date, end date, and frequency. Example shape: =FDSC("-",$G$6,"julian(P_PRICE("&$G$7&","&$G$8&",D).dates)").
- This correct julian(<series>.dates) convention is different from JULIAN(P_DATE(...)), which is invalid. Do not use P_DATE(), SEQUENCE, WORKDAY, or calendar arithmetic for the normal displayed Date column. The only permitted date-axis derivation is adding .dates and the julian wrapper to the exact returned value series; do not otherwise alter it.
- If the user explicitly requests calendar or workday rows, match each value to the exact FactSet-returned date with XLOOKUP or an equivalent exact lookup. Leave unmatched weekends, holidays, and missing observations blank; never align independent date/value spills by row position.
- julian(<series>.dates) returns true Excel date serials. Display the entire normal date-axis spill as dd-mmm-yyyy by default (for example, 10-Jul-2026), unless the user explicitly requests another date format.
- Plain <series>.dates remains available for raw/diagnostic YYYYMMDD keys. If explicitly requested, display those raw keys with 0000-00-00; do not apply an Excel serial-date format to them.
- Absolute-date input cells still use a normal Excel date number format. An ISO date supplied elsewhere in the prompt is context only, not a literal to inline into FQL.`;

const FDS_BOUND_FORMULA_PREFIXES = [
  "=FDS(",
  "=FDSB(",
  "=FDSC(",
  "=FDSRC(",
  "=FDSZ(",
  "=FDT(",
] as const;

export type FqlDatePolicyIssueCode =
  | "unsupported_fql_date_text_serialization"
  | "unsupported_fql_absolute_date_literal"
  | "unsupported_fql_display_date_axis";

export interface FqlDatePolicyIssue {
  code: FqlDatePolicyIssueCode;
  message: string;
  offendingText: string;
}

/**
 * Detect formulas whose values are resolved by the FactSet runtime and
 * therefore need FQL-compatible date serialization.
 */
export function isFdsBoundFormula(formula: unknown): formula is string {
  if (typeof formula !== "string") return false;
  const normalized = formula.trim().toUpperCase();
  return FDS_BOUND_FORMULA_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

/**
 * Return the first unsupported date representation in an FDS-family formula.
 * Non-FDS formulas are intentionally ignored so ordinary Excel date display
 * logic remains valid.
 *
 * This validator is reject-only. It never rewrites a model-authored or
 * FactSet-returned formula, because silently changing an opaque FQL string can
 * turn an explicit error into a wrong-but-plausible result.
 */
export function findFqlDatePolicyIssue(formula: unknown): FqlDatePolicyIssue | null {
  if (!isFdsBoundFormula(formula)) return null;

  const fqlArgument = extractFqlArgumentExpression(formula);
  if (!fqlArgument) return null;

  if (/\bJULIAN\s*\(\s*P_DATE\s*\(/i.test(fqlArgument)) {
    return {
      code: "unsupported_fql_display_date_axis",
      message:
        `JULIAN(P_DATE(...)) is not a valid displayed time-series date axis. ` +
        `Use julian(<the exact matching value-series expression>.dates) instead.`,
      offendingText: "JULIAN(P_DATE(...))",
    };
  }

  const textCallIndex = findExcelFunctionCall(fqlArgument, "TEXT");
  if (textCallIndex >= 0) {
    const maskMatch = fqlArgument
      .slice(textCallIndex)
      .match(/"([^"]*)"/);
    const offendingText = maskMatch?.[1] || "TEXT";
    return {
      code: "unsupported_fql_date_text_serialization",
      message:
        `FQL absolute dates must use the underlying numeric value of a real Excel date cell. ` +
        `Concatenate the date cell reference directly instead of serializing it with ` +
        `TEXT().`,
      offendingText,
    };
  }

  for (const staticFqlText of extractExcelStringLiterals(fqlArgument)) {
    const literalIssue = findUnsupportedAbsoluteDateLiteral(staticFqlText);
    if (literalIssue) {
      return {
        code: "unsupported_fql_absolute_date_literal",
        message:
          `FQL absolute date "${literalIssue}" is not a valid MM/DD/YYYY value. ` +
          `Prefer a real Excel date cell and concatenate its raw cell reference; ` +
          `otherwise use a calendar-valid MM/DD/YYYY literal returned by TTF.`,
        offendingText: literalIssue,
      };
    }
  }

  return null;
}

/** Return the parsed outer arguments of an FDS-family Excel formula. */
export function extractFdsFormulaArguments(formula: string): string[] | null {
  const prefix = formula.match(
    /^\s*=\s*(?:FDS|FDSB|FDSC|FDSRC|FDSZ|FDT)\s*\(/i,
  );
  if (!prefix) return null;

  const args: string[] = [];
  let current = "";
  let nestedDepth = 0;
  let inString = false;
  for (let index = prefix[0].length; index < formula.length; index += 1) {
    const char = formula[index];
    if (char === '"') {
      current += char;
      if (inString && formula[index + 1] === '"') {
        current += formula[index + 1];
        index += 1;
      } else {
        inString = !inString;
      }
      continue;
    }
    if (!inString && char === "(") {
      nestedDepth += 1;
      current += char;
      continue;
    }
    if (!inString && char === ")") {
      if (nestedDepth > 0) {
        nestedDepth -= 1;
        current += char;
        continue;
      }
      args.push(current.trim());
      break;
    }
    if (!inString && nestedDepth === 0 && char === ",") {
      args.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }

  if (inString || args.length < 2) return null;
  return args;
}

/**
 * Return the final outer argument of an FDS-family formula. This is the Excel
 * expression that builds the FQL string; earlier arguments are host options and
 * identifiers and must not be mistaken for FQL content.
 */
export function extractFqlArgumentExpression(formula: string): string | null {
  const args = extractFdsFormulaArguments(formula);
  return args?.[args.length - 1] || null;
}

/** Locate an Excel function call outside quoted string fragments. */
function findExcelFunctionCall(expression: string, functionName: string): number {
  let inString = false;
  for (let index = 0; index < expression.length; index += 1) {
    const char = expression[index];
    if (char === '"') {
      if (inString && expression[index + 1] === '"') {
        index += 1;
      } else {
        inString = !inString;
      }
      continue;
    }
    if (inString || !/[A-Za-z_]/.test(char)) continue;

    let end = index + 1;
    while (end < expression.length && /[A-Za-z0-9_.]/.test(expression[end])) {
      end += 1;
    }
    const candidate = expression.slice(index, end);
    let next = end;
    while (next < expression.length && /\s/.test(expression[next])) next += 1;
    if (
      candidate.toUpperCase() === functionName.toUpperCase() &&
      expression[next] === "("
    ) {
      return index;
    }
    index = end - 1;
  }
  return -1;
}

/** Decode the static Excel string fragments that make up the FQL argument. */
function extractExcelStringLiterals(expression: string): string[] {
  const literals: string[] = [];
  for (let index = 0; index < expression.length; index += 1) {
    if (expression[index] !== '"') continue;
    let value = "";
    index += 1;
    while (index < expression.length) {
      if (expression[index] !== '"') {
        value += expression[index];
        index += 1;
        continue;
      }
      if (expression[index + 1] === '"') {
        value += '"';
        index += 2;
        continue;
      }
      literals.push(value);
      break;
    }
  }
  return literals;
}

function isValidCalendarDate(year: number, month: number, day: number): boolean {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return false;
  }
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
}

/**
 * Return the first unsupported numeric or month-name date token. Valid US
 * slash dates are intentionally accepted for exact TTF-returned formulas.
 */
function findUnsupportedAbsoluteDateLiteral(value: string): string | null {
  const yearFirst = /\b\d{4}[-/]\d{1,2}[-/]\d{1,2}\b/.exec(value);
  if (yearFirst) return yearFirst[0];

  const monthName =
    /\b\d{1,2}-(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)-\d{2,4}\b/i.exec(
      value,
    );
  if (monthName) return monthName[0];

  const numericPattern = /\b(\d{1,2})([-/])(\d{1,2})\2(\d{2,4})\b/g;
  let match: RegExpExecArray | null;
  while ((match = numericPattern.exec(value)) !== null) {
    const month = Number(match[1]);
    const day = Number(match[3]);
    const yearText = match[4];
    if (
      match[2] !== "/" ||
      yearText.length !== 4 ||
      !isValidCalendarDate(Number(yearText), month, day)
    ) {
      return match[0];
    }
  }
  return null;
}
